import * as React from "react";
import { useQuery } from "convex/react";
import type { ColumnDef } from "@tanstack/react-table";
import { Download } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/convexApi";
import { pickAssortedBadgeClass } from "@/lib/badge-tones";
import { formatStatusLabel } from "@/lib/format";
import {
  exportToCSV,
  formatDateForExport,
  type ExportColumn,
} from "@/lib/export";
import { getInventoryStatusBadgeClass } from "@/lib/status-tag-colors";
import { matchesScopedFilter } from "./adminTabFilterUtils";

interface InventoryTabProps {
  authUserId?: string;
}

type PackageType = "bag" | "box";

interface CargoRow {
  id: string;
  productName: string;
  packageType: PackageType;
  palletLocation: string;
  status: string;
  totalBags: number;
  availableBags: number;
  soldBags: number;
  oci: string;
  purchaseNumber: string;
  contractNumber: string;
  origin: string;
  unitWeight: number;
  arrivalDate: string;
}

const DEFAULT_VISIBLE_STATUSES = [
  "at_origin",
  "upcoming",
  "in_transit",
  "in_spot",
] as const;

const INVENTORY_STATUS_ORDER: Record<string, number> = {
  at_origin: 0,
  upcoming: 1,
  in_transit: 2,
  in_spot: 3,
  sold_out: 4,
  delivered: 5,
  archived: 6,
  unknown: 7,
};

const STATUS_FILTER_OPTIONS = [
  { label: "At Origin", value: "at_origin" },
  { label: "Upcoming", value: "upcoming" },
  { label: "In Transit", value: "in_transit" },
  { label: "In Spot", value: "in_spot" },
  { label: "Sold Out", value: "sold_out" },
  { label: "Delivered", value: "delivered" },
  { label: "Archived", value: "archived" },
] as const;

const PACKAGE_TYPE_FILTER_OPTIONS = [
  { label: "Bags", value: "bag" },
  { label: "Boxes", value: "box" },
] as const;

function palletLocationToDisplay(value: unknown): string {
  if (typeof value === "string" && value.trim()) return value;
  return "N/A";
}

function resolveOriginName(cargo: any): string {
  const productOrigin = cargo?.product?.origin;
  if (typeof productOrigin === "string" && productOrigin.trim()) return productOrigin;
  if (typeof productOrigin?.name === "string" && productOrigin.name.trim()) {
    return productOrigin.name;
  }
  if (typeof cargo?.product?.originName === "string" && cargo.product.originName.trim()) {
    return cargo.product.originName;
  }
  if (typeof cargo?.origin === "string" && cargo.origin.trim()) return cargo.origin;
  if (typeof cargo?.country === "string" && cargo.country.trim()) return cargo.country;
  return "N/A";
}

function getStatusSortRank(status: unknown): number {
  const fallbackRank = INVENTORY_STATUS_ORDER.unknown ?? Number.MAX_SAFE_INTEGER;
  if (typeof status !== "string") return fallbackRank;
  return INVENTORY_STATUS_ORDER[status] ?? fallbackRank;
}

function formatPackageTypeLabel(packageType: PackageType): string {
  return packageType === "box" ? "Box" : "Bag";
}

function resolvePackageType(cargo: any): PackageType {
  const explicitPackageType = cargo?.product?.packageType ?? cargo?.packageType;
  if (explicitPackageType === "box" || explicitPackageType === "bag") {
    return explicitPackageType;
  }

  const unitWeight = cargo?.product?.weight ?? cargo?.unitWeight ?? cargo?.bagWeight;
  return typeof unitWeight === "number" && Number.isFinite(unitWeight) && unitWeight > 0 && unitWeight < 50
    ? "box"
    : "bag";
}

function useColumns(data: CargoRow[]) {
  const originOptions = React.useMemo(() => {
    const origins = [...new Set(data.map((r) => r.origin).filter((o) => o && o !== "N/A"))].sort();
    return origins.map((o) => ({ label: o, value: o }));
  }, [data]);

  return React.useMemo<ColumnDef<CargoRow>[]>(
    () => [
      {
        id: "productName",
        accessorKey: "productName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Product" />
        ),
        cell: ({ row }) => (
          <span className="text-xs font-medium">
            {row.original.productName}
          </span>
        ),
        meta: {
          label: "Product",
          placeholder: "Search product...",
          variant: "text" as const,
        },
        enableColumnFilter: true,
      },
      {
        id: "palletLocation",
        accessorKey: "palletLocation",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Pallet Location" />
        ),
        cell: ({ row }) =>
          row.original.palletLocation && row.original.palletLocation !== "N/A" ? (
            <Badge
              variant="outline"
              className={pickAssortedBadgeClass(row.original.palletLocation)}
            >
              {row.original.palletLocation}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">N/A</span>
          ),
        meta: {
          label: "Pallet Location",
          placeholder: "Search pallet location...",
          variant: "text" as const,
        },
        enableColumnFilter: true,
      },
      {
        id: "packageType",
        accessorKey: "packageType",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Package" />
        ),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={pickAssortedBadgeClass(row.original.packageType)}
          >
            {formatPackageTypeLabel(row.original.packageType)}
          </Badge>
        ),
        meta: {
          label: "Package",
          variant: "select" as const,
          placeholder: "Filter package...",
          options: [...PACKAGE_TYPE_FILTER_OPTIONS],
        },
        enableColumnFilter: true,
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Status" />
        ),
        cell: ({ row }) => (
          <Badge variant="outline" className={getInventoryStatusBadgeClass(row.original.status)}>
            {formatStatusLabel(row.original.status)}
          </Badge>
        ),
        meta: {
          label: "Status",
          variant: "multiSelect" as const,
          placeholder: "Filter status...",
          options: [...STATUS_FILTER_OPTIONS],
        },
        sortingFn: (left, right, columnId) =>
          getStatusSortRank(left.getValue(columnId)) - getStatusSortRank(right.getValue(columnId)),
        enableColumnFilter: true,
      },
      {
        id: "totalBags",
        accessorKey: "totalBags",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Total Units" />
        ),
        cell: ({ row }) => (
          <span className="text-xs font-medium tabular-nums">
            {row.original.totalBags.toLocaleString()}
          </span>
        ),
        meta: { label: "Total Units", variant: "range" as const },
        enableColumnFilter: true,
      },
      {
        id: "availableBags",
        accessorKey: "availableBags",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Available Units" />
        ),
        cell: ({ row }) => (
          <span className="text-xs tabular-nums">
            {row.original.availableBags.toLocaleString()}
          </span>
        ),
        meta: { label: "Available Units", variant: "range" as const },
        enableColumnFilter: true,
      },
      {
        id: "soldBags",
        accessorKey: "soldBags",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Sold Units" />
        ),
        cell: ({ row }) => (
          <span className="text-xs tabular-nums">
            {row.original.soldBags.toLocaleString()}
          </span>
        ),
        meta: { label: "Sold Units", variant: "range" as const },
        enableColumnFilter: true,
      },
      {
        id: "oci",
        accessorFn: (row) =>
          [row.oci, row.purchaseNumber, row.contractNumber]
            .filter((v) => v && v !== "N/A")
            .join(" "),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="OCI" />
        ),
        cell: ({ row }) => (
          <span className="text-xs">{row.original.oci}</span>
        ),
        meta: {
          label: "OCI",
          variant: "text" as const,
          placeholder: "Search OCI...",
        },
        enableColumnFilter: true,
      },
      {
        id: "purchaseNumber",
        accessorFn: (row) =>
          [row.purchaseNumber, row.contractNumber]
            .filter((v) => v && v !== "N/A")
            .join(" "),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="PO #" />
        ),
        cell: ({ row }) => (
          <span className="text-xs">{row.original.purchaseNumber}</span>
        ),
        meta: {
          label: "PO #",
          variant: "text" as const,
          placeholder: "Search PO...",
        },
        enableColumnFilter: true,
      },
      {
        id: "contractNumber",
        accessorFn: (row) =>
          [row.contractNumber, row.purchaseNumber]
            .filter((v) => v && v !== "N/A")
            .join(" "),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Contract #" />
        ),
        cell: ({ row }) => (
          <span className="text-xs">{row.original.contractNumber}</span>
        ),
        meta: {
          label: "Contract #",
          variant: "text" as const,
          placeholder: "Search contract...",
        },
        enableColumnFilter: true,
      },
      {
        id: "origin",
        accessorKey: "origin",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Origin" />
        ),
        cell: ({ row }) =>
          row.original.origin && row.original.origin !== "N/A" ? (
            <Badge
              variant="outline"
              className={pickAssortedBadgeClass(row.original.origin)}
            >
              {row.original.origin}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">N/A</span>
          ),
        meta: {
          label: "Origin",
          variant: "multiSelect" as const,
          placeholder: "Filter origin...",
          options: [...originOptions],
        },
        filterFn: (row, _columnId, filterValues: string[]) =>
          filterValues.some((v) => v === row.original.origin),
        enableColumnFilter: true,
      },
      {
        id: "unitWeight",
        accessorKey: "unitWeight",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Unit Weight" />
        ),
        cell: ({ row }) => (
          <span className="text-xs tabular-nums">
            {row.original.unitWeight} lbs
          </span>
        ),
        meta: { label: "Unit Weight", variant: "range" as const, unit: "lbs" },
        enableColumnFilter: true,
      },
      {
        id: "arrivalDate",
        accessorKey: "arrivalDate",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Arrival" />
        ),
        cell: ({ row }) => {
          if (!row.original.arrivalDate) return <span className="text-xs text-muted-foreground">-</span>;
          return (
            <span className="text-xs">
              {new Date(row.original.arrivalDate).toLocaleDateString()}
            </span>
          );
        },
        meta: {
          label: "Arrival",
          variant: "dateRange" as const,
        },
        enableColumnFilter: true,
      },
    ],
    [originOptions],
  );
}

export function InventoryTab({ authUserId }: InventoryTabProps) {
  const [searchParams] = useSearchParams();

  const cargosData = useQuery(
    api.admin.getCargosWithDetails,
    authUserId
      ? { authUserId, limit: 1000, includeArchived: false }
      : "skip",
  );

  const isLoading = cargosData === undefined;

  const tableData = React.useMemo((): CargoRow[] => {
    if (!cargosData || !Array.isArray(cargosData)) return [];
    return (cargosData as any[]).map((cargo: any, index: number) => ({
      id: cargo._id || `cargo-${index}`,
      productName: cargo.productName || cargo.product?.name || "Unknown",
      packageType: resolvePackageType(cargo),
        palletLocation: palletLocationToDisplay(cargo.palletLocation),
      status: cargo.status || "unknown",
      totalBags: cargo.totalBags || 0,
      availableBags: cargo.availableBags ?? cargo.totalBags ?? 0,
      soldBags: cargo.soldBags || 0,
      oci: cargo.ociNumber || cargo.oci || "N/A",
      purchaseNumber: cargo.cargoUuid || cargo.contractId || "N/A",
      contractNumber: cargo.contractId || "N/A",
      origin: resolveOriginName(cargo),
      unitWeight: cargo.product?.weight || cargo.unitWeight || cargo.bagWeight || 0,
      arrivalDate: cargo.arrivalDate
        ? new Date(cargo.arrivalDate).toISOString()
        : "",
    }));
  }, [cargosData]);

  const columns = useColumns(tableData);

  const ociParam = searchParams.get("oci");
  const productNameParam = searchParams.get("productName");

  const scopedTableData = React.useMemo(
    () =>
      tableData.filter((row) => {
        if (!matchesScopedFilter([row.oci], ociParam)) return false;
        if (!matchesScopedFilter([row.productName], productNameParam)) return false;
        return true;
      }),
    [tableData, ociParam, productNameParam],
  );

  const exportColumns = React.useMemo<ExportColumn[]>(
    () => [
      { key: "productName", label: "Product" },
      {
        key: "packageType",
        label: "Package",
        format: (value) => formatPackageTypeLabel(value === "box" ? "box" : "bag"),
      },
      { key: "palletLocation", label: "Pallet Location" },
      { key: "status", label: "Status" },
      { key: "totalBags", label: "Total Units" },
      { key: "availableBags", label: "Available Units" },
      { key: "soldBags", label: "Sold Units" },
      { key: "oci", label: "OCI" },
      { key: "purchaseNumber", label: "PO #" },
      { key: "contractNumber", label: "Contract #" },
      { key: "origin", label: "Origin" },
      { key: "unitWeight", label: "Unit Weight" },
      { key: "arrivalDate", label: "Arrival", format: formatDateForExport },
    ],
    [],
  );

  const exportRows = React.useCallback(
    (rows: CargoRow[], filename: string) => {
      if (rows.length === 0) {
        return;
      }
      exportToCSV(rows, exportColumns, filename);
    },
    [exportColumns],
  );

  const handleExport = React.useCallback(() => {
    exportRows(
      scopedTableData,
      `inventory-cargos-${new Date().toISOString().split("T")[0]}.csv`,
    );
  }, [exportRows, scopedTableData]);

  const handleExportSelected = React.useCallback(
    (selectedRows: CargoRow[]) => {
      exportRows(
        selectedRows,
        `inventory-cargos-selected-${new Date().toISOString().split("T")[0]}.csv`,
      );
    },
    [exportRows],
  );

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4">
          <h2 className="text-xs font-semibold">Inventory</h2>
          <p className="text-xs text-muted-foreground">
            Cargo lots across warehouses with bag and box separation, availability, and status tracking.
          </p>
        </div>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        ) : scopedTableData.length > 0 ? (
          <>
            <div className="mb-2 flex items-center justify-end">
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="mr-2 h-4 w-4" />
                Export All
              </Button>
            </div>
            <InternalEntityDataTable
              data={scopedTableData}
              columns={columns}
              pageSize={20}
              initialSorting={[
                { id: "status", desc: false },
                { id: "origin", desc: false },
                { id: "productName", desc: false },
                { id: "purchaseNumber", desc: false },
                { id: "contractNumber", desc: false },
                { id: "oci", desc: false },
              ]}
              initialColumnFilters={[
                { id: "status", value: [...DEFAULT_VISIBLE_STATUSES] },
              ]}
              actionBar={({ selectedRows, clearSelection }) => (
                <DataTableFloatingBar
                  selectedCount={selectedRows.length}
                  onClear={clearSelection}
                >
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExportSelected(selectedRows)}
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Export Selected
                  </Button>
                </DataTableFloatingBar>
              )}
            />
          </>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            No inventory data found
          </div>
        )}
      </CardContent>
    </Card>
  );
}
